<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.viewContact')"
            size="large">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.partner_contact_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="openAddContact">
                    {{ $t('labels.add') }}
                </hts-form-button>
            </div>
            <hts-data-table
                ref="dataTables"
                :headers="cooperatorContact"
                :server="server"
                store-name="cooperatorDataTable"
                store-key="CooperatorContact"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection />
            <add-contact
                ref="addContact"
                @refreshTables="refreshTables" />
            <hts-modal
                :show="showPopupDeleteContact"
                :title="$t('labels.deleteContact')"
                type="confirm"
                :content="$t('labels.confirmDeleteContact')"
                @hide="closePopupDeleteContact"
                @confirm="handleDeleteContact" />
        </hts-router-drawer>
        <router-view @refresh-tables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddContact from './contact/-components/add.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '~/data/permissions/layout-permissions'

import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'

const { $formatters } = useNuxtApp()

const visible = ref(false)
const dataTables = ref()
const cooperatorContactId = ref('')
const addContact: any = ref(null)
const editContact: any = ref(null)
const showPopupDeleteContact: any = ref(false)

const cooperatorCode = computed(() => useRoute().params.idpartner)

const server = {
  method: 'post',
  url: '/partner/contact/' + cooperatorCode.value + '/list'
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const openAddContact = () => {
  if (addContact.value) {
    addContact.value.open(cooperatorCode.value)
  }
}
const openEditContact = (contact: any) => {
  useRouter().push({ name: 'c-id-partner-idpartner-contact-idcontact-edit', params: { idcontact: contact._id } })
}
const openDeleteContact = (contact: any) => {
  cooperatorContactId.value = contact?._id
  showPopupDeleteContact.value = true
}

const closePopupDeleteContact = () => {
  showPopupDeleteContact.value = false
}

const cooperatorContact = [
  {
    field: 'fullName',
    headerName: $t('labels.fullName'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'phone',
    headerName: $t('labels.phone'),
    valueFormatter: (params: any) => {
      const code = params?.data?.phone.code || ''
      const phone = params?.data?.phone.phone || ''
      return code ? `(${code}) ${phone}` : phone
    }
  },
  {
    field: 'email',
    headerName: $t('labels.email'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'office',
    headerName: $t('labels.office'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'department',
    headerName: $t('labels.department'),
    valueGetter: (params: any) => {
      if (params.data.department) {
        switch (params.data.department) {
        case 'BUSINESS':
          return $t('labels.departments.business')
        case 'ACCOUNTANT':
          return $t('labels.departments.accountant')
        case 'PERSONNEL':
          return $t('labels.departments.personnel')
        case 'LOGISTIC':
          return $t('labels.departments.logistic')
        }
      }
      return ''
    }
  },
  {
    field: 'birthday',
    headerName: $t('labels.birthday'),
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.birthday) {
        return $formatters.date(params.data.birthday)
      }
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    filter: 'agDateColumnFilter',
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      storeKey: 'cooperatorDataTable',
      storeName: 'CooperatorContact',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_contact_edit),
          click: openEditContact
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.partner_contact_delete),
          click: openDeleteContact,
          isArchive: true
        }
      ]
    }
  }
]

const handleDeleteContact = () => {
  const api = {
    url: '/partner/contact/cooperatorId/delete/cooperatorContactId',
    method: 'delete'
  }
  $api(api, { uriParams: { cooperatorId: cooperatorCode.value, cooperatorContactId: cooperatorContactId.value } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteContactSuccess'))
      }
      refreshTables()
      showPopupDeleteContact.value = false
    })
    .catch(() => {
      showPopupDeleteContact.value = false
    })
}

const close = () => {
  visible.value = false
}

const useArchive = { api: $urlPartner.partner.archiveContact, permissions: checkPermission(layoutPermissions.partner_comtact_archive) }
const useRestore = { api: $urlPartner.partner.restoreContact, permissions: checkPermission(layoutPermissions.partner_contact_restore) }
</script>
